import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { SelectOutlined } from "@nova/component-select"
import { droneCatalog, DroneMake } from "@nova/constants"
import { InputOutlined, TextAreaOutlined } from "@nova/ds-component-input"
import { ButtonDestructive, ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { FunctionComponent, useState } from "react"
import { AircraftManualStatus } from "@nova/schemas"
import * as Icons from "@nova/component-icons"
import { AircraftComputedStatus } from "./AircraftStatusQueries"

const minPurchaseDateString = "2011-01-01"

const conditionOptions: { label: string; value: NonNullable<AircraftManualStatus> }[] = [
    { label: "Good to fly", value: "good_to_fly" },
    { label: "Needs maintenance", value: "needs_maintenance" },
    { label: "Grounded", value: "grounded" },
]

const conditionLabel = (status: AircraftComputedStatus): string => {
    if (status === "maintenance_due") return "Maintenance due"
    return conditionOptions.find((o) => o.value === status)?.label ?? ""
}

const manufacturerOptions = Object.entries(droneCatalog).map(([key, { displayName }]) => ({
    label: displayName,
    value: key,
}))

export interface AircraftFormValues {
    nickname: string
    manufacturer: string
    model: string
    serialNumber: string
    registrationNumber: string
    purchasedAt: string
    warrantyExpiresAt: string
    notes: string
    manualStatus: AircraftComputedStatus
}

interface AircraftFormModalProps {
    title: string
    submitLabel: string
    initialValues?: Partial<AircraftFormValues>
    requireManufacturerAndModel?: boolean
    isMutating: boolean
    onSubmit: (values: AircraftFormValues) => void
    onDelete?: () => void
    onCancel: () => void
}

const AircraftFormModal: FunctionComponent<AircraftFormModalProps> = ({
    title,
    submitLabel,
    initialValues,
    requireManufacturerAndModel = false,
    isMutating,
    onSubmit,
    onDelete,
    onCancel,
}) => {
    const [nickname, setNickname] = useState(initialValues?.nickname ?? "")
    const [manufacturer, setManufacturer] = useState(initialValues?.manufacturer ?? "")
    const [model, setModel] = useState(initialValues?.model ?? "")
    const [serialNumber, setSerialNumber] = useState(initialValues?.serialNumber ?? "")
    const [registrationNumber, setRegistrationNumber] = useState(
        initialValues?.registrationNumber ?? ""
    )
    const [purchasedAt, setPurchasedAt] = useState(initialValues?.purchasedAt ?? "")
    const [warrantyExpiresAt, setWarrantyExpiresAt] = useState(
        initialValues?.warrantyExpiresAt ?? ""
    )
    const [notes, setNotes] = useState(initialValues?.notes ?? "")
    const [manualStatus, setManualStatus] = useState<AircraftComputedStatus>(
        initialValues?.manualStatus ?? "good_to_fly"
    )

    const modelOptions =
        manufacturer && manufacturer in droneCatalog
            ? Object.entries(droneCatalog[manufacturer as DroneMake].models).map(
                  ([key, { displayName }]) => ({ label: displayName, value: key })
              )
            : []

    const canSubmit =
        !!serialNumber &&
        !isMutating &&
        (!requireManufacturerAndModel || (!!manufacturer && !!model))

    const handleManufacturerChange = (value: string) => {
        setManufacturer(value)
        setModel("")
    }

    const handleSubmit = () => {
        onSubmit({
            nickname,
            manufacturer,
            model,
            serialNumber,
            registrationNumber,
            purchasedAt,
            warrantyExpiresAt,
            notes,
            manualStatus,
        })
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-xl w-full"
            title={title}
            closeModal={onCancel}
            bottomBar={
                <div className="flex gap-2 w-full">
                    {onDelete && (
                        <ButtonDestructive
                            title="Delete Drone"
                            LeftIcon={Icons.Trash}
                            onClick={onDelete}
                            disabled={isMutating}
                        />
                    )}
                    <div className="grow" />
                    <ButtonTertiary title="Cancel" onClick={onCancel} />
                    <ButtonPrimary
                        title={submitLabel}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    />
                </div>
            }
        >
            <div className="grid grid-cols-2 gap-4 pt-2">
                <InputOutlined
                    label="Nickname"
                    value={nickname}
                    onChange={(e) => setNickname(e.currentTarget.value)}
                />

                <div className="flex flex-col gap-2">
                    <label className="text-gray-100">
                        Manufacturer{requireManufacturerAndModel ? "*" : ""}
                    </label>
                    <SelectOutlined
                        options={manufacturerOptions}
                        value={manufacturer}
                        onChange={handleManufacturerChange}
                        placeholder="Select manufacturer"
                        searchable
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <label className="text-gray-100">
                        Model{requireManufacturerAndModel ? "*" : ""}
                    </label>
                    <SelectOutlined
                        options={modelOptions}
                        value={model}
                        onChange={setModel}
                        placeholder="Select model"
                        disabled={!manufacturer}
                        searchable
                    />
                </div>

                <InputOutlined
                    label="Serial Number*"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.currentTarget.value)}
                />
                <InputOutlined
                    label="Registration Number"
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.currentTarget.value)}
                />

                <InputOutlined
                    type="date"
                    label="Purchase Date"
                    value={purchasedAt}
                    onChange={(e) => setPurchasedAt(e.currentTarget.value)}
                    min={minPurchaseDateString}
                />
                <InputOutlined
                    type="date"
                    label="Warranty Expires"
                    value={warrantyExpiresAt}
                    onChange={(e) => setWarrantyExpiresAt(e.currentTarget.value)}
                />

                <div className="flex flex-col gap-2">
                    <label className="text-gray-100">Condition</label>
                    <SelectOutlined<AircraftComputedStatus>
                        options={conditionOptions}
                        value={manualStatus}
                        getTitle={conditionLabel}
                        onChange={setManualStatus}
                        placeholder="Select condition"
                    />
                </div>

                <div className="col-span-2">
                    <TextAreaOutlined
                        label="Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.currentTarget.value)}
                        rows={4}
                    />
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export { AircraftFormModal }
